/**
 * RefineCodeWorkbenchMock — MOCK UI PROTOTYPE
 *
 * Purpose: Design review only. Zero backend calls. Fully isolated.
 * This component MUST NOT be wired to any real API, S3, or job state.
 * Safe to remove without touching any other flow.
 *
 * Layout: four-zone workbench
 *   [Top Summary Bar]
 *   [Left Panel | Center Editor | Right Panel]
 *
 * CSS prefix: rw-*
 * Based on design notes: 2026-05-12_refine_code_workbench_design_notes.md
 */

import { useState } from 'react'
import {
  CheckCircle2, XCircle, AlertTriangle, Download, ArrowRight,
  ChevronRight, ChevronDown, FileCode, Folder, FolderOpen,
  Code2, GitCompare, AlertCircle, BookOpen,
  Cpu, Lightbulb, Wrench, Info,
  Play, RotateCcw, Copy, Check
} from 'lucide-react'
import './RefineCodeWorkbenchMock.css'

/* ─── Mock Data ────────────────────────────────────────────────────────────── */

const MOCK_JOB = {
  job_id: 'rfne_acc_65506c09a03a_ClaimsModel9_20260512_151958_ab1c7888',
  app_name: 'ClaimsModel9',
  compile_status: 'PASSED',
  summary: {
    applied: 1,
    annotated: 0,
    needs_review: 1,
    compile_errors: 0,
    files_changed: 1,
    total_files: 5,
  },
}

const MOCK_FILES = [
  {
    path: 'services/CustomerMatching.java',
    status: 'modified',
    cobol_origin: 'CUSTUPD.cbl',
    patches: ['rec_001'],
    compile_errors: [],
  },
  {
    path: 'shared/Custupd.java',
    status: 'unchanged',
    cobol_origin: 'CUSTUPD.cbl',
    patches: [],
    compile_errors: [],
  },
  {
    path: 'interfaces/ICustvalService.java',
    status: 'unchanged',
    cobol_origin: null,
    patches: [],
    compile_errors: [],
  },
  {
    path: 'entities/CustmastInLine.java',
    status: 'unchanged',
    cobol_origin: 'CUSTMAST.cbl',
    patches: [],
    compile_errors: [],
  },
  {
    path: 'repositories/CustomerRepository.java',
    status: 'unchanged',
    cobol_origin: null,
    patches: [],
    compile_errors: [],
  },
]

const MOCK_PATCHES = [
  {
    recommendation_id: 'rec_001',
    smell_type: 'DEAD_PARAGRAPH',
    title: 'Dead paragraph — remove or consolidate',
    target_file: 'services/CustomerMatching.java',
    status: 'applied',
    lines_changed: 20,
    reason: "Added @Deprecated annotation to method 'main' (COBOL paragraph: MAIN)",
    cobol_paragraph: 'MAIN',
    cobol_origin: 'CUSTUPD.cbl',
    patch_complexity: 'Moderate',
  },
  {
    recommendation_id: 'rec_002',
    smell_type: 'COBOL_NAMING',
    title: 'Rename COBOL-style identifier to Java camelCase',
    target_file: 'shared/Custupd.java',
    status: 'no_match',
    lines_changed: 0,
    reason: "Whole-word replace of 'LINE' → 'custmastInLine': 0 replacements (token already in camelCase)",
    cobol_paragraph: null,
    cobol_origin: 'CUSTUPD.cbl',
    patch_complexity: 'Simple',
  },
]

const MOCK_COBOL_SNIPPET = `      *-----------------------------------------------
       IDENTIFICATION DIVISION.
       PROGRAM-ID. CUSTUPD.

       PROCEDURE DIVISION.
       MAIN.
           PERFORM VALIDATE-INPUT
           PERFORM UPDATE-RECORD
           STOP RUN.

       VALIDATE-INPUT.
           IF CUSTMAST-IN-LINE = SPACES
               MOVE 'N' TO WS-VALID-FLAG
           END-IF.

       UPDATE-RECORD.
           EXEC SQL
               UPDATE CUSTMAST
               SET CUST-NAME = :WS-CUST-NAME
               WHERE CUST-ID = :WS-CUST-ID
           END-EXEC.`

/* Mock Java source for CustomerMatching.java — patched version with @Deprecated on main() */
const MOCK_JAVA_SOURCE = `package com.claimsmodel9.services;

import com.claimsmodel9.entities.CustmastInLine;
import com.claimsmodel9.repositories.CustomerRepository;
import org.springframework.stereotype.Service;

/**
 * CustomerMatching — migrated from COBOL program CUSTUPD.
 * Handles customer record update logic.
 *
 * @see CustmastInLine
 * @see CustomerRepository
 */
@Service
public class CustomerMatching {

    private final CustomerRepository customerRepository;

    public CustomerMatching(CustomerRepository customerRepository) {
        this.customerRepository = customerRepository;
    }

    /**
     * Entry point migrated from COBOL paragraph: MAIN.
     * MAIN was never PERFORMed externally — retained for traceability.
     *
     * @deprecated Dead paragraph from COBOL source (CUSTUPD.cbl / MAIN).
     *             Review before removing. Patch: rec_001 / DEAD_PARAGRAPH.
     */
    @Deprecated
    public void main() {
        validateInput();
        updateRecord();
    }

    /**
     * Migrated from COBOL paragraph: VALIDATE-INPUT.
     */
    public void validateInput() {
        CustmastInLine record = customerRepository.findCurrent();
        if (record == null || record.getCustmastInLine() == null
                || record.getCustmastInLine().isBlank()) {
            record.setValidFlag("N");
        }
    }

    /**
     * Migrated from COBOL paragraph: UPDATE-RECORD.
     */
    public void updateRecord() {
        customerRepository.updateCustomerName();
    }
}`

/* Line metadata: which lines are patched */
const PATCHED_LINES = new Set([29, 30, 31, 32, 33, 34, 35, 36, 37, 38])

/* ─── Sub-components ────────────────────────────────────────────────────────── */

function CompileStatusBadge({ status }) {
  if (status === 'PASSED') {
    return (
      <span className="rw-compile-badge rw-compile-passed">
        <CheckCircle2 size={13} />
        Compile Passed
      </span>
    )
  }
  return (
    <span className="rw-compile-badge rw-compile-failed">
      <XCircle size={13} />
      Compile Failed
    </span>
  )
}

function SummaryCount({ label, value, variant }) {
  return (
    <div className={`rw-summary-count rw-summary-count--${variant}`}>
      <span className="rw-summary-count-value">{value}</span>
      <span className="rw-summary-count-label">{label}</span>
    </div>
  )
}

/* ─── Left Panel ────────────────────────────────────────────────────────────── */

function LeftPanel({ activeFile, onSelectFile, leftTab, onSetLeftTab }) {
  const [expandedFolders, setExpandedFolders] = useState({
    'services': true,
    'shared': true,
    'interfaces': false,
    'entities': false,
    'repositories': false,
  })

  const grouped = MOCK_FILES.reduce((acc, f) => {
    const parts = f.path.split('/')
    const folder = parts.length > 1 ? parts[0] : '(root)'
    const file = parts[parts.length - 1]
    if (!acc[folder]) acc[folder] = []
    acc[folder].push({ ...f, filename: file })
    return acc
  }, {})

  const toggleFolder = (folder) =>
    setExpandedFolders(prev => ({ ...prev, [folder]: !prev[folder] }))

  return (
    <div className="rw-left-panel">
      <div className="rw-panel-tabs">
        {['Files', 'Changes', 'Issues'].map(t => (
          <button
            key={t}
            className={`rw-panel-tab ${leftTab === t ? 'active' : ''}`}
            onClick={() => onSetLeftTab(t)}
          >
            {t}
          </button>
        ))}
      </div>

      <div className="rw-panel-content">
        {leftTab === 'Files' && (
          <div className="rw-file-tree">
            {Object.entries(grouped).map(([folder, files]) => (
              <div key={folder} className="rw-tree-folder">
                <button
                  className="rw-tree-folder-row"
                  onClick={() => toggleFolder(folder)}
                >
                  {expandedFolders[folder]
                    ? <FolderOpen size={14} className="rw-folder-icon" />
                    : <Folder size={14} className="rw-folder-icon" />}
                  <span className="rw-tree-folder-name">{folder}/</span>
                  {expandedFolders[folder]
                    ? <ChevronDown size={12} className="rw-chevron" />
                    : <ChevronRight size={12} className="rw-chevron" />}
                </button>
                {expandedFolders[folder] && files.map(f => (
                  <button
                    key={f.path}
                    className={`rw-tree-file-row ${activeFile?.path === f.path ? 'active' : ''}`}
                    onClick={() => onSelectFile(f)}
                  >
                    <FileCode size={13} className="rw-file-icon" />
                    <span className="rw-tree-file-name">{f.filename}</span>
                    {f.status === 'modified' && (
                      <span className="rw-file-badge rw-file-badge--modified">M</span>
                    )}
                  </button>
                ))}
              </div>
            ))}
          </div>
        )}

        {leftTab === 'Changes' && (
          <div className="rw-changes-list">
            <div className="rw-changes-section">
              <div className="rw-changes-section-header rw-changes-section-header--applied">
                <CheckCircle2 size={13} />
                Applied ({MOCK_PATCHES.filter(p => p.status === 'applied').length})
              </div>
              {MOCK_PATCHES.filter(p => p.status === 'applied').map(p => (
                <div key={p.recommendation_id} className="rw-change-item rw-change-item--applied">
                  <div className="rw-change-smell">{p.smell_type}</div>
                  <div className="rw-change-title">{p.title}</div>
                  <div className="rw-change-file">{p.target_file.split('/').pop()}</div>
                </div>
              ))}
            </div>
            <div className="rw-changes-section">
              <div className="rw-changes-section-header rw-changes-section-header--review">
                <AlertTriangle size={13} />
                Needs Review ({MOCK_PATCHES.filter(p => p.status === 'no_match').length})
              </div>
              {MOCK_PATCHES.filter(p => p.status === 'no_match').map(p => (
                <div key={p.recommendation_id} className="rw-change-item rw-change-item--review">
                  <div className="rw-change-smell">{p.smell_type}</div>
                  <div className="rw-change-title">{p.title}</div>
                  <div className="rw-change-file">{p.target_file.split('/').pop()}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {leftTab === 'Issues' && (
          <div className="rw-issues-list">
            <div className="rw-issues-empty">
              <CheckCircle2 size={28} className="rw-issues-empty-icon" />
              <span>No compile errors</span>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

/* ─── Center Panel (Java Editor) ────────────────────────────────────────────── */

function CenterPanel({ activeFile }) {
  const [copied, setCopied] = useState(false)
  const lines = MOCK_JAVA_SOURCE.split('\n')

  const handleCopy = () => {
    navigator.clipboard.writeText(MOCK_JAVA_SOURCE).catch(() => {})
    setCopied(true)
    setTimeout(() => setCopied(false), 1500)
  }

  if (!activeFile || activeFile.status === 'unchanged') {
    return (
      <div className="rw-center-panel">
        <div className="rw-editor-placeholder">
          <FileCode size={36} className="rw-editor-placeholder-icon" />
          {activeFile
            ? <><span>{activeFile.path}</span><span className="rw-editor-placeholder-sub">No changes — file is identical to the SvcGen output</span></>
            : <span>Select a file from the panel on the left</span>}
        </div>
      </div>
    )
  }

  return (
    <div className="rw-center-panel">
      {/* Editor tab bar */}
      <div className="rw-editor-tabbar">
        <div className="rw-editor-tab active">
          <FileCode size={13} />
          <span>{activeFile.path.split('/').pop()}</span>
          <span className="rw-editor-tab-modified" />
        </div>
        <div className="rw-editor-tabbar-actions">
          <button className="rw-editor-action" onClick={handleCopy} title="Copy source">
            {copied ? <Check size={13} /> : <Copy size={13} />}
          </button>
        </div>
      </div>

      {/* Patch highlight legend */}
      <div className="rw-editor-legend">
        <span className="rw-legend-item rw-legend-item--patch">
          <span className="rw-legend-swatch" />
          rec_001 · DEAD_PARAGRAPH · @Deprecated added
        </span>
      </div>

      {/* Code editor */}
      <div className="rw-editor-body">
        <div className="rw-editor-gutter">
          {lines.map((_, i) => (
            <div
              key={i}
              className={`rw-gutter-line ${PATCHED_LINES.has(i + 1) ? 'rw-gutter-line--patched' : ''}`}
            >
              {i + 1}
            </div>
          ))}
        </div>
        <div className="rw-editor-code">
          {lines.map((line, i) => {
            const lineNum = i + 1
            const isPatched = PATCHED_LINES.has(lineNum)
            return (
              <div
                key={i}
                className={`rw-code-line ${isPatched ? 'rw-code-line--patched' : ''}`}
              >
                {isPatched && <span className="rw-patch-marker" title="Patched by rec_001" />}
                <span className={`rw-code-text ${getLineTokenClass(line)}`}>
                  {renderTokenizedLine(line)}
                </span>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

/* Very lightweight syntax coloring — no external lib, pure CSS class tagging */
function getLineTokenClass(line) {
  const t = line.trimStart()
  if (t.startsWith('//') || t.startsWith('*') || t.startsWith('/**') || t.startsWith('/*')) return 'rw-code-comment'
  return ''
}

function renderTokenizedLine(line) {
  /* Minimal token split: annotations, keywords, strings — good enough for mock */
  const ANNOTATION_RE = /(@\w+)/g
  const KEYWORD_RE = /\b(public|private|protected|class|interface|import|package|return|new|void|null|true|false|if|else|final|static|extends|implements|throws|this|super|deprecated)\b/g
  const STRING_RE = /("(?:[^"\\]|\\.)*")/g

  /* Replace in order: strings → annotations → keywords */
  const parts = []
  let rest = line
  let lastIndex = 0
  const tokens = []

  /* Collect all match ranges with type */
  const allMatches = []
  let m

  const strRe = new RegExp(STRING_RE.source, 'g')
  while ((m = strRe.exec(line)) !== null) allMatches.push({ start: m.index, end: m.index + m[0].length, type: 'string', text: m[0] })

  const annRe = new RegExp(ANNOTATION_RE.source, 'g')
  while ((m = annRe.exec(line)) !== null) {
    const overlap = allMatches.some(a => m.index >= a.start && m.index < a.end)
    if (!overlap) allMatches.push({ start: m.index, end: m.index + m[0].length, type: 'annotation', text: m[0] })
  }

  const kwRe = new RegExp(KEYWORD_RE.source, 'g')
  while ((m = kwRe.exec(line)) !== null) {
    const overlap = allMatches.some(a => m.index >= a.start && m.index < a.end)
    if (!overlap) allMatches.push({ start: m.index, end: m.index + m[0].length, type: 'keyword', text: m[0] })
  }

  allMatches.sort((a, b) => a.start - b.start)

  let cursor = 0
  const result = []
  for (const tok of allMatches) {
    if (tok.start > cursor) result.push(<span key={cursor}>{line.slice(cursor, tok.start)}</span>)
    result.push(<span key={tok.start} className={`rw-tok-${tok.type}`}>{tok.text}</span>)
    cursor = tok.end
  }
  if (cursor < line.length) result.push(<span key={cursor}>{line.slice(cursor)}</span>)

  return result.length ? result : line
}

/* ─── Right Panel ───────────────────────────────────────────────────────────── */

function RightPanel({ activeFile, rightTab, onSetRightTab }) {
  const patch = activeFile ? MOCK_PATCHES.find(p => p.target_file === activeFile.path) : null
  const [aiPrompt, setAiPrompt] = useState('')
  const [aiResponse, setAiResponse] = useState(null)
  const [aiLoading, setAiLoading] = useState(false)

  const handleAiSend = (prompt) => {
    setAiLoading(true)
    setAiResponse(null)
    setTimeout(() => {
      setAiLoading(false)
      setAiResponse(getMockAiResponse(prompt || aiPrompt))
      setAiPrompt('')
    }, 900)
  }

  return (
    <div className="rw-right-panel">
      <div className="rw-panel-tabs">
        {['Patch', 'COBOL', 'AI Assist', 'Compile'].map(t => (
          <button
            key={t}
            className={`rw-panel-tab ${rightTab === t ? 'active' : ''}`}
            onClick={() => onSetRightTab(t)}
          >
            {t}
          </button>
        ))}
      </div>

      <div className="rw-panel-content">
        {rightTab === 'Patch' && (
          <div className="rw-patch-detail">
            {patch ? (
              <>
                <div className={`rw-patch-status-row rw-patch-status--${patch.status === 'applied' ? 'applied' : 'review'}`}>
                  {patch.status === 'applied'
                    ? <><CheckCircle2 size={14} /> Applied</>
                    : <><AlertTriangle size={14} /> Needs Review</>}
                </div>
                <div className="rw-detail-block">
                  <div className="rw-detail-label">Recommendation</div>
                  <div className="rw-detail-value">{patch.recommendation_id}</div>
                </div>
                <div className="rw-detail-block">
                  <div className="rw-detail-label">Smell Type</div>
                  <div className="rw-detail-value rw-smell-badge">{patch.smell_type}</div>
                </div>
                <div className="rw-detail-block">
                  <div className="rw-detail-label">Title</div>
                  <div className="rw-detail-value">{patch.title}</div>
                </div>
                <div className="rw-detail-block">
                  <div className="rw-detail-label">Outcome</div>
                  <div className="rw-detail-value rw-detail-value--muted">{patch.reason}</div>
                </div>
                {patch.lines_changed > 0 && (
                  <div className="rw-detail-block">
                    <div className="rw-detail-label">Lines Changed</div>
                    <div className="rw-detail-value">{patch.lines_changed}</div>
                  </div>
                )}
                <div className="rw-detail-block">
                  <div className="rw-detail-label">Complexity</div>
                  <div className="rw-detail-value">{patch.patch_complexity}</div>
                </div>
                {patch.cobol_paragraph && (
                  <div className="rw-detail-block">
                    <div className="rw-detail-label">COBOL Paragraph</div>
                    <div className="rw-detail-value rw-cobol-ref">{patch.cobol_paragraph}</div>
                  </div>
                )}
              </>
            ) : (
              <div className="rw-right-empty">
                <GitCompare size={24} />
                <span>Select a modified file to see patch detail</span>
              </div>
            )}

            {/* All patches summary below */}
            <div className="rw-patch-summary-divider">All Patches</div>
            {MOCK_PATCHES.map(p => (
              <div key={p.recommendation_id} className={`rw-patch-row rw-patch-row--${p.status === 'applied' ? 'applied' : 'review'}`}>
                <div className="rw-patch-row-id">{p.recommendation_id}</div>
                <div className="rw-patch-row-smell">{p.smell_type}</div>
                <div className={`rw-patch-row-status`}>
                  {p.status === 'applied' ? 'Applied' : 'No match'}
                </div>
              </div>
            ))}
          </div>
        )}

        {rightTab === 'COBOL' && (
          <div className="rw-cobol-panel">
            {activeFile?.cobol_origin ? (
              <>
                <div className="rw-detail-block">
                  <div className="rw-detail-label">Source Program</div>
                  <div className="rw-detail-value rw-cobol-ref">{activeFile.cobol_origin}</div>
                </div>
                {patch?.cobol_paragraph && (
                  <div className="rw-detail-block">
                    <div className="rw-detail-label">Paragraph</div>
                    <div className="rw-detail-value rw-cobol-ref">{patch.cobol_paragraph}</div>
                  </div>
                )}
                <div className="rw-cobol-snippet-label">COBOL Source</div>
                <div className="rw-cobol-snippet">
                  <pre>{MOCK_COBOL_SNIPPET}</pre>
                </div>
              </>
            ) : (
              <div className="rw-right-empty">
                <BookOpen size={24} />
                <span>No COBOL grounding available for this file</span>
              </div>
            )}
          </div>
        )}

        {rightTab === 'AI Assist' && (
          <div className="rw-ai-panel">
            <div className="rw-ai-mock-notice">
              <Info size={13} />
              Mock UI — AI calls are simulated
            </div>

            <div className="rw-ai-quick-actions">
              <div className="rw-ai-quick-label">Quick Actions</div>
              <button className="rw-ai-quick-btn" onClick={() => handleAiSend('explain')}>
                <Lightbulb size={13} /> Explain this method
              </button>
              <button className="rw-ai-quick-btn" onClick={() => handleAiSend('fix')}>
                <Wrench size={13} /> Suggest safer patch
              </button>
              <button className="rw-ai-quick-btn" onClick={() => handleAiSend('compare')}>
                <GitCompare size={13} /> Compare Java to COBOL
              </button>
              <button className="rw-ai-quick-btn" onClick={() => handleAiSend('why')}>
                <Info size={13} /> Why was this flagged?
              </button>
            </div>

            <div className="rw-ai-chat-area">
              {aiLoading && (
                <div className="rw-ai-loading">
                  <Cpu size={14} className="rw-ai-loading-icon" />
                  Thinking…
                </div>
              )}
              {aiResponse && !aiLoading && (
                <div className="rw-ai-response">
                  <div className="rw-ai-response-label">
                    <Cpu size={12} /> AI
                  </div>
                  <div className="rw-ai-response-text">{aiResponse}</div>
                </div>
              )}
              {!aiLoading && !aiResponse && (
                <div className="rw-ai-chat-empty">
                  Ask a question or use a quick action above
                </div>
              )}
            </div>

            <div className="rw-ai-input-row">
              <input
                className="rw-ai-input"
                placeholder="Ask about this code…"
                value={aiPrompt}
                onChange={e => setAiPrompt(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && aiPrompt.trim() && handleAiSend()}
              />
              <button
                className="rw-ai-send-btn"
                onClick={() => aiPrompt.trim() && handleAiSend()}
                disabled={!aiPrompt.trim()}
              >
                <Play size={13} />
              </button>
            </div>
          </div>
        )}

        {rightTab === 'Compile' && (
          <div className="rw-compile-panel">
            <div className="rw-compile-result rw-compile-result--passed">
              <CheckCircle2 size={18} />
              <div>
                <div className="rw-compile-result-title">Maven Compile Passed</div>
                <div className="rw-compile-result-sub">0 errors · 0 warnings</div>
              </div>
            </div>
            <div className="rw-detail-block">
              <div className="rw-detail-label">Compiled At</div>
              <div className="rw-detail-value">2026-05-12T15:20:19Z</div>
            </div>
            <div className="rw-detail-block">
              <div className="rw-detail-label">Output</div>
              <div className="rw-compile-log">
                <pre>[INFO] Scanning for projects...{'\n'}[INFO] Building ClaimsModel9 1.0.0{'\n'}[INFO] --- maven-compiler-plugin:3.11:compile ---{'\n'}[INFO] Nothing to compile - all classes are up to date{'\n'}[INFO] BUILD SUCCESS{'\n'}[INFO] Total time: 3.241 s</pre>
              </div>
            </div>
            <button className="rw-recompile-btn">
              <RotateCcw size={13} />
              Re-run Compile
              <span className="rw-mock-tag">mock</span>
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

/* ─── Mock AI Responses ─────────────────────────────────────────────────────── */

function getMockAiResponse(prompt) {
  const p = (prompt || '').toLowerCase()
  if (p.includes('explain')) {
    return "The main() method was annotated @Deprecated because the COBOL paragraph MAIN was never PERFORMed externally — it acted as the entry point but was only reached via the program entry, not via COBOL PERFORM. In Java, this maps to a method that should either be removed or marked for review. The patch chose the safer path: annotate and preserve rather than delete."
  }
  if (p.includes('fix') || p.includes('patch')) {
    return "A safer patch would rename main() to processEntry() and keep the @Deprecated annotation. This avoids a future clash with Java's standard main(String[]) entry point and makes the COBOL origin clearer. After renaming, update any internal call sites and re-run compile."
  }
  if (p.includes('compare') || p.includes('cobol')) {
    return "COBOL MAIN paragraph maps to Java main(). The PERFORM VALIDATE-INPUT → validateInput(), PERFORM UPDATE-RECORD → updateRecord(). The STOP RUN is implicit in Java (method return). The structure is preserved — only the identifier style and lifecycle annotation differ."
  }
  if (p.includes('why') || p.includes('flagged')) {
    return "DEAD_PARAGRAPH is flagged when a COBOL paragraph is never PERFORMed from outside its own program. In this case, MAIN serves as the program entry point rather than a callable subroutine. The Java equivalent has no direct entry-point concept, so the method becomes dead code unless explicitly called. The recommendation is to review whether it should be deleted, renamed, or wired to a Spring @PostConstruct or similar lifecycle hook."
  }
  return "This is a mock AI response. In the real workbench, this would call POST /refine-code/{job_id}/ai/explain or /ai/suggest-fix with the current file content and cursor context."
}

/* ─── Top Summary Bar ───────────────────────────────────────────────────────── */

function TopBar({ job, onDownload, onContinue }) {
  return (
    <div className="rw-topbar">
      <div className="rw-topbar-left">
        <div className="rw-topbar-title">
          <Code2 size={16} className="rw-topbar-icon" />
          Refine Code Checkpoint
          <span className="rw-mock-banner">MOCK</span>
        </div>
        <div className="rw-topbar-app">{job.app_name}</div>
      </div>

      <div className="rw-topbar-center">
        <CompileStatusBadge status={job.compile_status} />
        <SummaryCount label="Applied" value={job.summary.applied} variant="applied" />
        <SummaryCount label="Needs Review" value={job.summary.needs_review} variant="review" />
        <SummaryCount label="Files Changed" value={job.summary.files_changed} variant="neutral" />
        <SummaryCount label="Total Files" value={job.summary.total_files} variant="neutral" />
      </div>

      <div className="rw-topbar-right">
        <button className="rw-btn rw-btn-secondary" onClick={onDownload}>
          <Download size={14} />
          Download Refined Code
        </button>
        <button className="rw-btn rw-btn-primary" onClick={onContinue}>
          Continue to Build / Package
          <ArrowRight size={14} />
        </button>
      </div>
    </div>
  )
}

/* ─── Root Component ────────────────────────────────────────────────────────── */

export default function RefineCodeWorkbenchMock() {
  const [activeFile, setActiveFile] = useState(MOCK_FILES[0])
  const [leftTab, setLeftTab] = useState('Files')
  const [rightTab, setRightTab] = useState('Patch')
  const [toastMsg, setToastMsg] = useState(null)

  const showToast = (msg) => {
    setToastMsg(msg)
    setTimeout(() => setToastMsg(null), 2800)
  }

  const handleDownload = () => showToast('Download triggered (mock) — would stream refined-code-package.zip')
  const handleContinue = () => showToast('Continue triggered (mock) — would navigate to Build / Package node')

  const handleSelectFile = (file) => {
    setActiveFile(file)
    /* Auto-switch right panel to Patch if file has patches */
    if (file.patches?.length) setRightTab('Patch')
    else if (file.cobol_origin) setRightTab('COBOL')
  }

  return (
    <div className="rw-root">
      <TopBar job={MOCK_JOB} onDownload={handleDownload} onContinue={handleContinue} />

      <div className="rw-workspace">
        <LeftPanel
          activeFile={activeFile}
          onSelectFile={handleSelectFile}
          leftTab={leftTab}
          onSetLeftTab={setLeftTab}
        />
        <CenterPanel activeFile={activeFile} />
        <RightPanel
          activeFile={activeFile}
          rightTab={rightTab}
          onSetRightTab={setRightTab}
        />
      </div>

      {toastMsg && (
        <div className="rw-toast">
          <Info size={14} />
          {toastMsg}
        </div>
      )}
    </div>
  )
}
